import React, { useState, useEffect } from "react";
import flaser from "object-flaser/lib/flaser";
import { FlexboxGrid } from "rsuite";
import styled from "styled-components";

import { PAYMENT_DETAIL_CRYPTO_LIST_API } from "const/apiMethods";
import { checkPermissionsFor } from "store/storeHelpers";
import { SCREEN_MEDIA } from "const";
import { ButtonContent } from "components/base/BaseButton";
import { Spacer } from "components/base/Spacer";
import ModalRemovePaymentDetail from "./ModalRemovePaymentDetail";
import PaymentDetailsTable from "./PaymentDetailsTable";
import ModalAddPayment from "./ModalAddPaymentDetail";

const { sm, md } = SCREEN_MEDIA;


const PaymentDetails = ({
    accountId,
    data,
    curKey,
    loading,
    permissions,
    organizationTypeList,
    countryList,
    cryptoList,
    paymentMethodList,
    getPaymentDetailsList,
    getPaymentDetailsSingle,
    getPaymentDetailsCryptoList,
    createPaymentDetails,
    modifyPaymentDetails,
    removePaymentDetails,
    allowGetList = true
}) => {

    const [showModalAddPaymentDetail, setShowModalAddPaymentDetail] = useState(false);
    const [showModalRemovePaymentDetail, setShowModalRemovePaymentDetail] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isModify, setIsModify] = useState(false);
    const [addPaymentDefaultValue, setAddPaymentDefaultValue] = useState(null);
    const [loadingEditElementsSet, setLoadingEditElementsSet] = useState(new Set());

    useEffect(() => {
        if (allowGetList && accountId) {
            getPaymentDetailsList({ target: { account_id: accountId } });
        }
    }, [accountId, allowGetList]);

    useEffect(() => {
        if (checkPermissionsFor(PAYMENT_DETAIL_CRYPTO_LIST_API)) {
            getPaymentDetailsCryptoList();
        }
    }, [permissions]);

    const onShowModalAddPaymentDetail = () => {
        setShowModalAddPaymentDetail(true);
    };

    const closeModalAddPaymentDetail = () => {
        setIsModify(false);
        setAddPaymentDefaultValue(false);
        setShowModalAddPaymentDetail(false);
    };

    const handleEditPaymentDetails = async (value) => {
        const { id } = value;

        // set elements loading status
        const editElements = new Set(loadingEditElementsSet);
        setLoadingEditElementsSet(editElements.add(id));

        const response = await getPaymentDetailsSingle({ target: { payment_detail_id: id } });

        // remove elements loading status
        if (editElements.has(id)) {
            editElements.delete(id);
            setLoadingEditElementsSet(editElements);
        }

        if (!response && response.payment_detail) {
            return;
        }

        const fetchedData = response.payment_detail;

        let files = null;
        if (fetchedData.ext_files) {
            files = [];
            for (const file of fetchedData.ext_files) {
                files.push({
                    url: `data:${file.type};base64,${file.file}`,
                    fileKey: file.file_id,
                    name: file.name,
                });
            }
        }

        const defaultValue = {
            ...fetchedData,
            ext_files: files
        };

        setIsModify(true);
        setAddPaymentDefaultValue(flaser(defaultValue));
        setShowModalAddPaymentDetail(true);
    };

    const handleRemovePaymentDetails = (value) => {
        setSelectedItem(value);
        setShowModalRemovePaymentDetail(true);
    };

    return (
        <>
            <FlexboxGrid justify="end">
                <FlexboxGrid.Item>
                    <StyledActionButtonWrapper>
                        <ActionButton
                            onClick={onShowModalAddPaymentDetail}
                        >
                            + Add new
                        </ActionButton>
                    </StyledActionButtonWrapper>
                </FlexboxGrid.Item>
            </FlexboxGrid>

            <Spacer />

            <PaymentDetailsTable
                data={data}
                loading={loading || !allowGetList}
                loadingEditButtonsSet={loadingEditElementsSet}
                {...{
                    handleRemovePaymentDetails,
                    handleEditPaymentDetails
                }}
            />

            <ModalAddPayment
                show={showModalAddPaymentDetail}
                onClose={closeModalAddPaymentDetail}
                defaultFormValue={isModify ? addPaymentDefaultValue : null}
                modify={isModify}
                {...{
                    curKey,
                    accountId,
                    cryptoList,
                    organizationTypeList,
                    countryList,
                    paymentMethodList,
                    getPaymentDetailsList,
                    createPaymentDetails,
                    modifyPaymentDetails,
                }}
            />

            <ModalRemovePaymentDetail
                show={showModalRemovePaymentDetail}
                onClose={() => {
                    setShowModalRemovePaymentDetail(false)
                }}
                {...{
                    accountId,
                    selectedItem,
                    getPaymentDetailsList,
                    removePaymentDetails
                }}
            />

        </>
    );
};

export default PaymentDetails;

const StyledActionButtonWrapper = styled.div`
    text-align: center;
    
    @media (min-width: 768px) {
        text-align: right;
    }
`;


export const ActionButton = styled(ButtonContent).attrs(() => {
    return {
        size: "md",
        buttonStyle: "secondary"
    };
})`
    && {
        padding: ${props => props.isLink ? "0" : "12px 20px"};
        width: auto;
    
        .rs-icon {
            margin-right: 5px;
        }
    
        @media (min-width: ${sm.min}px) {
            min-width: 160px;
        }
        
        @media (min-width: ${md.min}px) {
            padding: ${props => props.isLink ? "0" : "8px 12px"};
        }
    }
`;